/**
 * Guards against "NotFoundError: The object can not be found here" crashes.
 *
 * Third-party scripts that mutate the DOM outside React's control (session
 * recording tools like PostHog/rrweb, browser translate/password-manager
 * extensions) can remove or move a node before React's own commit phase
 * tries to remove/reposition it. WebKit (desktop Safari and all iOS
 * browsers, since they're WebKit under the hood) throws in that case
 * instead of silently no-oping, which crashes the whole React tree.
 *
 * This makes removeChild/insertBefore no-ops when the DOM has already
 * diverged from what the caller expected, instead of throwing.
 */
export const patchDomMutations = () => {
	if (typeof Node === 'undefined' || !Node.prototype) return;

	const originalRemoveChild = Node.prototype.removeChild;
	Node.prototype.removeChild = function <T extends Node>(
		this: Node,
		child: T
	): T {
		if (child.parentNode !== this) {
			return child;
		}
		return originalRemoveChild.call(this, child) as T;
	};

	const originalInsertBefore = Node.prototype.insertBefore;
	Node.prototype.insertBefore = function <T extends Node>(
		this: Node,
		newNode: T,
		referenceNode: Node | null
	): T {
		if (referenceNode && referenceNode.parentNode !== this) {
			return newNode;
		}
		return originalInsertBefore.call(this, newNode, referenceNode) as T;
	};
};
